import { assetUrl } from "@/lib/api";

/**
 * Contenu éditorial de la rubrique « Conseils & inspirations ».
 * Pour ajouter un article : compléter ARTICLES ci-dessous.
 * Types de blocs disponibles : h2, h3, p, list {items}, callout {title, text}, steps {items:[{title,text}]}.
 */

export const CATEGORIES = [
  "Préparer son projet",
  "Budget & travaux",
  "Aménagement & inspiration",
  "Comprendre son chantier",
];

export const ARTICLES = [
  {
    slug: "definir-budget-travaux",
    title: "Comment définir son budget travaux ?",
    category: "Budget & travaux",
    excerpt:
      "Avant de parler matériaux, plans ou décoration, il faut savoir jusqu'où votre projet peut aller. Découvrez comment construire un budget réaliste sans oublier les dépenses qui entourent les travaux.",
    img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=85",
    imgAlt: "Plans de maison et outils de mesure posés sur une table de travail",
    metaDescription:
      "Budget disponible, coût des travaux, dépenses annexes, marge pour les imprévus : apprenez à construire un budget travaux réaliste et à hiérarchiser vos priorités.",
    intro:
      "Avant de choisir une cuisine, un sol ou de comparer des devis, une question revient toujours : combien ce projet peut-il réellement coûter ? Prendre le temps de définir son budget n'est pas une contrainte supplémentaire — c'est ce qui permettra ensuite de faire les bons choix, au bon moment.",
    blocks: [
      { type: "h2", text: "Pourquoi commencer par le budget ?" },
      {
        type: "p",
        text: "Le budget est le fil conducteur d'un projet de travaux. Il oriente les matériaux envisageables, l'ampleur des transformations possibles et le calendrier. Un projet qui démarre avec une enveloppe claire avance plus sereinement : chaque décision peut être rapprochée d'une vision d'ensemble, au lieu d'être prise à l'aveugle.",
      },
      {
        type: "p",
        text: "À l'inverse, un budget flou conduit souvent aux mêmes écueils : des travaux qui s'arrêtent à mi-parcours, des arbitrages subis dans l'urgence, ou des postes oubliés qui apparaissent en cours de route.",
      },
      { type: "h2", text: "Les quatre composantes d'un budget réaliste" },
      { type: "h3", text: "Le budget disponible" },
      {
        type: "p",
        text: "C'est le montant total que vous pouvez et souhaitez consacrer au projet : épargne, financement éventuel, aides auxquelles vous pouvez prétendre. Ce chiffre vous appartient — il constitue le cadre dans lequel tout le reste devra s'organiser.",
      },
      { type: "h3", text: "Le coût des travaux" },
      {
        type: "p",
        text: "C'est la partie la plus visible : la main-d'œuvre et les matériaux nécessaires à la réalisation. Il varie fortement selon la surface concernée, l'état de l'existant, la localisation du projet, le niveau de finition souhaité, les matériaux choisis, la complexité technique et les professionnels sollicités. Deux projets en apparence identiques peuvent donc aboutir à des montants très différents.",
      },
      { type: "h3", text: "Les dépenses annexes" },
      {
        type: "p",
        text: "Elles sont régulièrement sous-estimées : études préalables, plans, démarches administratives, relogement temporaire si le logement devient difficilement habitable, finitions et mobilier, ou encore la remise en état des abords. Les identifier tôt évite les mauvaises surprises.",
      },
      { type: "h3", text: "La marge pour les imprévus" },
      {
        type: "p",
        text: "Dans un bâtiment existant, l'ouverture d'un mur ou la dépose d'un revêtement révèle parfois une réalité différente de celle imaginée. Prévoir une marge — généralement de l'ordre de 5 à 15 % de l'enveloppe travaux selon l'ancienneté et la complexité du projet — permet d'absorber ces aléas sans remettre tout le projet en question.",
      },
      { type: "h2", text: "Indispensable, souhaitable, plus tard" },
      {
        type: "p",
        text: "Une fois l'enveloppe définie, la question n'est plus « que puis-je faire ? » mais « dans quel ordre ? ». Une méthode simple consiste à classer chaque poste en trois catégories :",
      },
      {
        type: "list",
        items: [
          "Indispensable : ce qui conditionne le confort, la sécurité ou la bonne tenue du logement — par exemple une toiture, un réseau électrique vieillissant ou une isolation défaillante.",
          "Souhaitable : ce qui améliore nettement le quotidien mais peut attendre quelques mois — une cuisine repensée, une salle de bains refaite.",
          "Réalisable plus tard : ce qui relève du plaisir ou de l'optimisation — aménagement des combles, terrasse, dressing sur mesure.",
        ],
      },
      {
        type: "p",
        text: "Ce classement ne fige rien : il vous donne une grille de lecture pour arbitrer sereinement si le budget doit être ajusté en cours de projet.",
      },
      {
        type: "callout",
        title: "Le conseil CozyHome",
        text: "Commencez par définir votre enveloppe globale avant de répartir votre budget pièce par pièce. Cela permet de conserver une vision d'ensemble du projet.",
      },
    ],
    conclusion:
      "Un budget n'est pas seulement un chiffre. C'est un outil pour faire les bons arbitrages tout au long du projet.",
    related: ["preparer-renovation", "choisir-professionnels", "choisir-materiaux"],
  },
  {
    slug: "preparer-renovation",
    title: "Bien préparer une rénovation",
    category: "Préparer son projet",
    excerpt:
      "Observer l'existant, définir ses objectifs, hiérarchiser les travaux : les réflexes qui font la différence avant le premier devis.",
    img: "https://images.pexels.com/photos/3214064/pexels-photo-3214064.jpeg?auto=compress&cs=tinysrgb&w=1080",
    imgAlt: "Carnet de notes et plans lors de la préparation d'un projet de rénovation",
    metaDescription:
      "Observer l'existant, définir ses objectifs, hiérarchiser les travaux, anticiper les contraintes techniques : les étapes clés pour bien préparer une rénovation.",
    intro:
      "Une rénovation réussie se joue rarement pendant les travaux : elle se prépare en amont, lorsque rien n'est encore décidé. Voici les grandes étapes d'une préparation méthodique, ni technique ni intimidante.",
    blocks: [
      { type: "h2", text: "Observer l'existant" },
      {
        type: "p",
        text: "Avant d'imaginer le futur, prenez le temps de comprendre le présent. Comment les espaces sont-ils organisés ? Qu'est-ce qui fonctionne bien au quotidien, et qu'est-ce qui vous gêne vraiment ? Notez également l'état des éléments invisibles : réseaux, isolation, menuiseries, humidité éventuelle. Cet état des lieux honnête est la base de toutes les décisions suivantes.",
      },
      { type: "h2", text: "Définir ses objectifs" },
      {
        type: "p",
        text: "Que cherchez-vous exactement ? Plus de lumière, une pièce supplémentaire, moins de déperdition de chaleur, une circulation plus simple ? Formuler clairement deux ou trois objectifs prioritaires évite que le projet ne s'éparpille — et permet d'évaluer chaque proposition à cette lumière.",
      },
      { type: "h2", text: "Hiérarchiser les travaux" },
      {
        type: "p",
        text: "Tout ne peut pas toujours être fait en même temps, et tout n'a pas la même urgence. Distinguez ce qui relève de la structure ou de la sécurité, ce qui améliore le confort, et ce qui relève de l'agrément. Un projet bien hiérarchisé peut être réalisé par étapes cohérentes, sans avoir à repasser deux fois au même endroit.",
      },
      { type: "h2", text: "Réfléchir aux usages futurs" },
      {
        type: "p",
        text: "Une rénovation engage le logement pour des années. Projettez-vous : famille qui grandit, télétravail, parents qui vieillissent, revente possible ? Une pièce pensée pour évoluer — bureau aujourd'hui, chambre demain — offre une souplesse précieuse sans coût supplémentaire au départ.",
      },
      { type: "h2", text: "Établir un budget" },
      {
        type: "p",
        text: "Vient ensuite la question de l'enveloppe : combien pouvez-vous et souhaitez-vous investir, dépenses annexes et marge pour les imprévus comprises ? Le montant des travaux dépendra de nombreux paramètres — surface, état existant, localisation, niveau de finition, matériaux, complexité et professionnels sollicités. Un budget réaliste, même approximatif au départ, sert de cadre à toutes les décisions.",
      },
      { type: "h2", text: "Anticiper les contraintes techniques" },
      {
        type: "p",
        text: "Certains éléments ne se déplacent pas facilement : un mur porteur, une colonne d'évacuation, une poutre, un compteur. Les repérer tôt permet d'adapter les envies à la réalité du bâti, plutôt que de découvrir une contrainte majeure une fois les plans finalisés.",
      },
      { type: "h2", text: "Préparer les différentes étapes" },
      {
        type: "p",
        text: "Enfin, organisez la séquence : études et plans éventuels, demandes d'autorisation si nécessaire, devis, choix des matériaux, calendrier des travaux, organisation de la vie quotidienne pendant le chantier. Chaque étape préparée en amont en simplifie deux en aval.",
      },
      {
        type: "callout",
        title: "Le conseil CozyHome",
        text: "Avant de choisir les couleurs ou les matériaux, commencez par comprendre ce que votre logement permet réellement de transformer.",
      },
    ],
    conclusion:
      "Bien préparer, ce n'est pas retarder le projet : c'est lui donner les meilleures chances de se dérouler comme vous l'imaginez.",
    related: ["definir-budget-travaux", "etapes-chantier", "choisir-professionnels"],
  },
  {
    slug: "choisir-professionnels",
    title: "Comment choisir les bons professionnels ?",
    category: "Préparer son projet",
    excerpt:
      "Devis détaillés, comparaison du contenu, informations à vérifier : repères simples pour choisir sereinement.",
    img: assetUrl("/api/static/redesign/conseil-pros.webp"),
    imgAlt: "Échange entre un particulier et un professionnel autour d'un projet de travaux",
    metaDescription:
      "Comment comparer des devis travaux au-delà du montant final : contenu détaillé, matériaux, délais, organisation et traces écrites pour choisir les bons professionnels.",
    intro:
      "Face à deux ou trois devis, l'écart des montants peut déconcerter. Pourtant, choisir un professionnel ne se résume pas à retenir le chiffre le plus bas. Quelques repères simples suffisent à comparer sérieusement et à faire un choix éclairé.",
    blocks: [
      { type: "h2", text: "Définir précisément le besoin" },
      {
        type: "p",
        text: "Un devis ne peut être pertinent que s'il répond à une demande claire. Avant de solliciter les professionnels, décrivez ce que vous souhaitez : pièces concernées, travaux envisagés, niveau de finition attendu, contraintes éventuelles. Plus la demande est précise, plus les propositions seront comparables entre elles.",
      },
      { type: "h2", text: "Demander des devis suffisamment détaillés" },
      {
        type: "p",
        text: "Un devis sérieux décrit les travaux poste par poste : nature des interventions, quantités, matériaux envisagés, prestations incluses. Un montant global sans détail ne permet aucune comparaison réelle — et laisse place à l'interprétation une fois le chantier commencé.",
      },
      { type: "h2", text: "Comparer le contenu, pas uniquement le montant final" },
      {
        type: "p",
        text: "Deux devis d'un montant proche peuvent couvrir des réalités très différentes : qualité des matériaux, nombre de passages, préparation des supports, nettoyage de fin de chantier. À l'inverse, un écart important doit vous amener à questionner : qu'est-ce qui explique cette différence ? L'objectif n'est pas de choisir le devis le moins cher, mais celui qui correspond le mieux à votre projet.",
      },
      {
        type: "callout",
        title: "Le point de repère",
        text: "Un bon devis doit permettre de comprendre ce qui sera réalisé, avec quels matériaux et dans quelles conditions.",
      },
      { type: "h2", text: "Vérifier les informations professionnelles pertinentes" },
      {
        type: "p",
        text: "Quelques vérifications simples sécurisent votre choix : l'existence et l'ancienneté de l'entreprise, ses assurances en cours de validité, des références de chantiers comparables au vôtre, et si possible un avis de clients ayant mené un projet similaire. Ces informations sont légitimes à demander — un professionnel sérieux les fournit sans difficulté.",
      },
      { type: "h2", text: "Comprendre ce qui est inclus — et ce qui ne l'est pas" },
      {
        type: "p",
        text: "Les malentendus naissent souvent des zones grises : la dépose de l'ancien revêtement est-elle comprise ? Les finitions ? La fourniture des équipements ? Faites préciser par écrit ce que le montant couvre, et ce qui resterait à votre charge ou ferait l'objet d'un avenant.",
      },
      { type: "h2", text: "Clarifier les délais et l'organisation" },
      {
        type: "p",
        text: "Demandez comment le chantier sera organisé : date de démarrage envisagée, durée estimée, ordre des interventions, présence d'un interlocuteur unique. Un calendrier réaliste et assumé vaut mieux qu'une promesse trop belle pour être tenue.",
      },
      { type: "h2", text: "Conserver une trace écrite des éléments importants" },
      {
        type: "p",
        text: "Les échanges téléphoniques sont utiles, mais les décisions importantes méritent un écrit : modifications acceptées, ajustements de prix, engagements sur les délais. Ces traces évitent bien des incompréhensions et sécurisent les deux parties.",
      },
    ],
    conclusion:
      "Choisir un professionnel, c'est avant tout choisir une relation de confiance documentée : des échanges clairs, un devis compréhensible et des engagements écrits.",
    related: ["definir-budget-travaux", "preparer-renovation", "etapes-chantier"],
  },
  {
    slug: "choisir-materiaux",
    title: "Choisir ses matériaux sans se perdre",
    category: "Aménagement & inspiration",
    excerpt:
      "Usage, esthétique, entretien, budget : quatre critères simples pour trancher entre des dizaines d'options séduisantes.",
    img: assetUrl("/api/static/models/canopee-int2.webp"),
    imgAlt: "Cuisine contemporaine mêlant bois clair et façades mates",
    metaDescription:
      "Sol, plan de travail, salle de bains, peinture, menuiseries : quatre critères simples pour choisir ses matériaux sans se perdre entre esthétique, usage et budget.",
    intro:
      "Carrelage ou parquet ? Stratifié ou pierre ? Face à l'abondance de catalogues et d'inspirations, le choix des matériaux peut vite devenir la partie la plus vertigineuse du projet. La bonne nouvelle : quatre critères suffisent à éclaircir la plupart des décisions.",
    blocks: [
      { type: "h2", text: "Quatre critères pour décider" },
      { type: "h3", text: "L'usage" },
      {
        type: "p",
        text: "C'est le critère qui passe avant tous les autres. Une entrée traversée vingt fois par jour, une salle de bains familiale ou une cuisine très utilisée n'appellent pas les mêmes matériaux qu'une chambre d'amis. Commencez toujours par la question : comment cette surface va-t-elle vivre ?",
      },
      { type: "h3", text: "L'esthétique" },
      {
        type: "p",
        text: "Le plaisir visuel compte, évidemment. Mais gardez en tête que le matériau le plus séduisant en photo n'est pas nécessairement le plus adapté à votre usage — et qu'un matériau plus simple, bien posé et bien associé, produit souvent un rendu plus durable qu'une tendance appliquée à contresens.",
      },
      { type: "h3", text: "L'entretien" },
      {
        type: "p",
        text: "Chaque matériau demande un niveau d'attention différent : certains se patinent avec grâce, d'autres marquent au premier contact. Soyez honnête sur le temps d'entretien que vous êtes prêt à y consacrer — c'est un critère de confort autant que de longévité.",
      },
      { type: "h3", text: "Le budget" },
      {
        type: "p",
        text: "À esthétique comparable, les écarts de prix peuvent être considérables, en fourniture comme en pose. Le coût dépend de la surface à couvrir, de l'état du support, de la localisation, du niveau de finition et des professionnels sollicités. Comparer à surface équivalente, pose comprise, change souvent le classement.",
      },
      { type: "h2", text: "Quelques exemples concrets" },
      { type: "h3", text: "Le sol" },
      {
        type: "p",
        text: "Pièce de vie très fréquentée ? Un carrelage ou un parquet contrecollé robuste accompagnera mieux les années qu'un bois tendre. Chambre ? Le confort d'un parquet ou d'un sol plus doux reprend l'avantage, l'usure y étant moindre.",
      },
      { type: "h3", text: "Le plan de travail" },
      {
        type: "p",
        text: "Si vous cuisinez beaucoup, privilégiez la résistance à la chaleur et aux taches. Une cuisine d'appoint peut s'offrir davantage de liberté esthétique. Dans tous les cas, demandez comment le matériau vieillit : c'est souvent plus utile que son aspect le premier jour.",
      },
      { type: "h3", text: "La salle de bains" },
      {
        type: "p",
        text: "Humidité permanente, nettoyage fréquent : la priorité va aux matériaux pensés pour cet environnement et à une pose soignée. Les effets de matière les plus tendance s'apprécient d'abord à l'aune de leur tenue dans le temps.",
      },
      { type: "h3", text: "La peinture" },
      {
        type: "p",
        text: "Au-delà de la couleur, c'est la finition qui change tout : mate pour masquer les imperfections d'un mur ancien, satinée ou veloutée là où les murs doivent se lessiver, comme une cuisine ou une chambre d'enfant.",
      },
      { type: "h3", text: "Les menuiseries" },
      {
        type: "p",
        text: "Fenêtres et portes combinent isolation, sécurité, entretien et esthétique. Bois, aluminium ou mixte : le bon choix dépend de votre exposition, de votre rapport à l'entretien et du caractère du bâti — pas seulement de l'effet en façade.",
      },
      {
        type: "callout",
        title: "Le conseil CozyHome",
        text: "Pensez usage avant tendance.",
      },
    ],
    conclusion:
      "Un bon matériau n'est pas le plus spectaculaire : c'est celui qui correspond à votre façon de vivre, et qui vous plaira encore dans dix ans.",
    related: ["definir-budget-travaux", "preparer-renovation", "reception-travaux"],
  },
  {
    slug: "etapes-chantier",
    title: "Comprendre les grandes étapes d'un chantier",
    category: "Comprendre son chantier",
    excerpt:
      "Préparation, réseaux, cloisons, finitions : dans quel ordre s'enchaînent les interventions, et pourquoi cela compte.",
    img: assetUrl("/api/static/chantiers/demo/murs.webp"),
    imgAlt: "Murs en cours de construction sur un chantier de maison",
    metaDescription:
      "Préparation, dépose, gros œuvre, réseaux, cloisons, revêtements, équipements, vérifications : comprenez les grandes étapes d'un chantier de rénovation ou de construction.",
    intro:
      "Un chantier peut donner l'impression d'un mouvement permanent un peu chaotique. En réalité, les interventions suivent une logique assez constante : chaque étape prépare la suivante. La comprendre, c'est déjà mieux suivre l'avancement de votre projet.",
    blocks: [
      {
        type: "steps",
        items: [
          { title: "Préparation", text: "Plans, choix techniques, commandes des matériaux longs à obtenir, protection du logement et des espaces. Une bonne partie du chantier se joue ici, avant le premier coup d'outil." },
          { title: "Organisation", text: "Planification des interventions, accès au chantier, stockage, coordination entre les différents corps de métier." },
          { title: "Dépose ou démolition éventuelle", text: "Retrait de l'existant : cloisons à supprimer, anciens revêtements, équipements hors d'usage. L'étape qui révèle parfois des surprises à intégrer au plan de route." },
          { title: "Gros œuvre si nécessaire", text: "Ouverture de murs porteurs, création de structures, extension, reprises de maçonnerie. Tout ce qui touche à la structure passe avant le reste." },
          { title: "Réseaux et lots techniques", text: "Électricité, plomberie, ventilation, chauffage : ces réseaux se mettent en place pendant que les murs restent accessibles." },
          { title: "Isolation et cloisons", text: "On referme ensuite : isolation, nouvelles cloisons, plafonds. Les espaces retrouvent leurs contours définitifs." },
          { title: "Revêtements et finitions", text: "Sols, peintures, carrelage : la partie la plus visible du chantier, qui n'arrive qu'une fois les supports prêts." },
          { title: "Équipements", text: "Pose de la cuisine, de la salle de bains, des menuiseries intérieures et des appareillages." },
          { title: "Vérifications finales", text: "Essais de fonctionnement, retouches, nettoyage, et tour complet du projet avant de le considérer terminé." },
        ],
      },
      { type: "h2", text: "Pourquoi l'ordre compte" },
      {
        type: "p",
        text: "Cette séquence n'a rien d'arbitraire : on ne peint pas avant d'avoir passé les réseaux, on ne pose pas un parquet avant d'avoir refermé les cloisons. Respecter l'ordre évite de dégrader ce qui vient d'être fait — c'est ce qui explique que certaines décisions, comme l'emplacement d'une prise ou d'un point d'eau, doivent être prises très tôt.",
      },
      {
        type: "p",
        text: "Bien sûr, l'ordre exact varie selon la nature du projet : une rénovation de salle de bains ne ressemble pas à une extension ou à une construction neuve. Mais la logique générale — préparer, structurer, équiper, fermer, finir, vérifier — reste la même.",
      },
    ],
    conclusion:
      "Comprendre ces étapes ne vous demande pas de devenir expert : cela vous permet simplement de suivre votre chantier avec des repères, et de poser les bonnes questions au bon moment.",
    related: ["reception-travaux", "preparer-renovation", "choisir-professionnels"],
  },
  {
    slug: "reception-travaux",
    title: "Bien préparer la réception de ses travaux",
    category: "Comprendre son chantier",
    excerpt:
      "Finitions, équipements, documents utiles : prendre le temps d'observer le résultat avant de considérer le projet terminé.",
    img: assetUrl("/api/static/models/ecrin-int1.webp"),
    imgAlt: "Séjour lumineux et abouti après la fin des travaux",
    metaDescription:
      "Avant de considérer votre projet terminé : finitions, équipements, fonctionnement, documents utiles et points à traiter. Nos repères pour préparer la réception des travaux.",
    intro:
      "Le chantier touche à sa fin, les espaces ressemblent enfin à ce que vous aviez imaginé. Avant de considérer le projet comme terminé, il reste une étape utile : prendre le temps d'observer les travaux réalisés et de comparer le résultat à ce qui était prévu.",
    blocks: [
      { type: "h2", text: "Prendre le temps d'observer" },
      {
        type: "p",
        text: "L'envie de tourner la page est compréhensible après des semaines de chantier. Pourtant, une visite méthodique — sans se presser, idéalement à la lumière du jour — permet de repérer sereinement ce qui mérite une retouche pendant que les professionnels restent mobilisés sur le projet.",
      },
      { type: "h2", text: "Les points à regarder" },
      { type: "h3", text: "Les finitions" },
      {
        type: "p",
        text: "Joints, alignements, peintures, raccords entre les matériaux : parcourez chaque pièce en prenant le temps de regarder de près. Les petits défauts se voient mieux avant l'emménagement qu'après.",
      },
      { type: "h3", text: "Les équipements" },
      {
        type: "p",
        text: "Vérifiez que les équipements posés correspondent bien à ce qui était prévu : modèles, coloris, nombre d'éléments. Les échanges ont parfois évolué en cours de chantier — c'est le moment de s'assurer que tout le monde parle de la même chose.",
      },
      { type: "h3", text: "Le fonctionnement" },
      {
        type: "p",
        text: "Ouvrez et fermez portes, fenêtres et tiroirs. Faites couler l'eau, testez les prises, les interrupteurs, le chauffage, la ventilation. Un équipement se juge à l'usage, pas seulement à l'aspect.",
      },
      { type: "h3", text: "La comparaison avec ce qui était prévu" },
      {
        type: "p",
        text: "Reprenez les devis, plans et échanges écrits : ce qui a été réalisé correspond-il à ce qui avait été convenu ? C'est aussi simple que cela — et c'est plus facile à vérifier tant que tout est frais en mémoire.",
      },
      { type: "h2", text: "Les documents utiles" },
      {
        type: "p",
        text: "Profitez de cette étape pour rassembler ce qui vous servira ensuite : notices des équipements, garanties éventuelles, plans des réseaux, factures. Ces documents prennent de la valeur avec le temps — pour l'entretien, de futurs travaux ou une revente.",
      },
      { type: "h2", text: "Les points restant à traiter" },
      {
        type: "p",
        text: "S'il reste des retouches ou des ajustements, notez-les précisément, avec photos si utile, et convenez par écrit d'une manière de les traiter. Une liste claire et partagée est la façon la plus simple de terminer un projet dans de bonnes conditions pour tout le monde.",
      },
      {
        type: "callout",
        title: "À garder en tête",
        text: "Cet article partage des repères pratiques pour préparer cette étape. Il ne remplace pas un conseil juridique : pour toute question de nature contractuelle ou litigieuse, rapprochez-vous d'un professionnel compétent.",
      },
    ],
    conclusion:
      "Une réception bien préparée n'est pas un moment de suspicion : c'est simplement la dernière étape d'un projet mené avec soin, du premier plan à la dernière retouche.",
    related: ["etapes-chantier", "choisir-professionnels", "definir-budget-travaux"],
  },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);

export const countWords = (article) => {
  const parts = [article.title, article.intro, article.conclusion || ""];
  for (const b of article.blocks) {
    if (b.text) parts.push(b.text);
    if (b.title) parts.push(b.title);
    if (b.items) {
      for (const it of b.items) parts.push(typeof it === "string" ? it : `${it.title} ${it.text}`);
    }
  }
  return parts.join(" ").split(/\s+/).filter(Boolean).length;
};

export const readingTime = (article) => Math.max(2, Math.round(countWords(article) / 200));
